import React, { useState, useEffect } from 'react';
import { adminApi } from '@/services/api';
import { Plus, Pencil, Trash2, Save, X, Layers } from 'lucide-react';
import { toast } from 'sonner';

const slugify = (s) => s.toLowerCase().trim()
  .replace(/[áàä]/g, 'a').replace(/[éèë]/g, 'e').replace(/[íìï]/g, 'i')
  .replace(/[óòö]/g, 'o').replace(/[úùü]/g, 'u').replace(/ñ/g, 'n')
  .replace(/[^a-z0-9\s-]/g, '').replace(/[\s-]+/g, '-');

const initialForm = { nombre: '', slug: '', orden: 99, activo: true };

const AdminFamilies = () => {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadFamilies(); }, []);

  const loadFamilies = async () => {
    try {
      const res = await adminApi.getFamilies();
      setFamilies(res.data);
    } catch { toast.error('Error al cargar familias'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowCreate(false);
  };

  const startEdit = (fam) => {
    setForm({ nombre: fam.nombre, slug: fam.slug, orden: fam.orden, activo: fam.activo });
    setEditingId(fam.id);
    setShowCreate(false);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    const payload = { ...form, slug: form.slug.trim() || slugify(form.nombre) };
    try {
      if (editingId) {
        await adminApi.updateFamily(editingId, payload);
        toast.success('Familia actualizada');
      } else {
        await adminApi.createFamily(payload);
        toast.success('Familia creada');
      }
      resetForm();
      loadFamilies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (fam) => {
    const msg = fam.productos_count > 0
      ? `Esta familia tiene ${fam.productos_count} producto(s) asociado(s). Se quedarán sin familia. ¿Eliminar?`
      : '¿Eliminar esta familia?';
    if (!window.confirm(msg)) return;
    try {
      await adminApi.deleteFamily(fam.id);
      toast.success('Familia eliminada');
      loadFamilies();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="p-6" data-testid="admin-families-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-steel-dark uppercase tracking-wider flex items-center gap-2">
            <Layers size={24} /> Familias
          </h1>
          <p className="text-steel-gray mt-1">Categorías de productos (familias)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} /> Nueva Familia
        </button>
      </div>

      {(showCreate || editingId) && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
          <h3 className="font-bold text-lg text-steel-dark mb-4">
            {editingId ? 'Editar Familia' : 'Nueva Familia'}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Nombre *</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value, slug: form.slug || slugify(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                placeholder="Ej: Perfiles"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                placeholder="auto-generado"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Orden</label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 99 })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  className="w-4 h-4 accent-accent-primary"
                />
                <span className="text-sm text-steel-gray">Activo</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 disabled:opacity-50">
              <Save size={16} /> {editingId ? 'Actualizar' : 'Crear'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-steel-gray hover:text-steel-dark transition-colors flex items-center gap-2">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-steel-gray">Cargando...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-steel-dark text-white text-left">
              <tr>
                <th className="px-4 py-3 text-sm font-medium w-16">Orden</th>
                <th className="px-4 py-3 text-sm font-medium">Nombre</th>
                <th className="px-4 py-3 text-sm font-medium">Slug</th>
                <th className="px-4 py-3 text-sm font-medium w-28">Productos</th>
                <th className="px-4 py-3 text-sm font-medium w-24">Estado</th>
                <th className="px-4 py-3 text-sm font-medium w-28">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {families.map(fam => (
                <tr key={fam.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-steel-gray">{fam.orden}</td>
                  <td className="px-4 py-3 font-medium text-steel-dark">{fam.nombre}</td>
                  <td className="px-4 py-3 text-sm text-steel-gray font-mono">{fam.slug}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="bg-accent-primary/10 text-accent-primary font-bold px-2 py-1 rounded text-xs">
                      {fam.productos_count}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${fam.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {fam.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(fam)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(fam)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {families.length === 0 && (
            <div className="text-center py-12 text-steel-gray">No hay familias registradas</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFamilies;
